import { useState, useEffect } from 'react'
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'

/**
 * 8-stage lifecycle progress map for a project.
 * Drop in anywhere with: <ProjectLifecycleMap project={project} />
 *
 * Computes the current stage from project status + related contracts/milestones/invoices,
 * so the map reflects real progress rather than just the textual status.
 */

const STAGES = [
  { id: 1, name: 'Intake', short: 'Created', detail: 'Project created (by PM or via TOR upload).' },
  { id: 2, name: 'Approval', short: 'Approved', detail: 'Admin approves to move project In Progress.' },
  { id: 3, name: 'Assignment', short: 'Assigned', detail: 'An Active consultant is assigned to the project.' },
  { id: 4, name: 'Contract', short: 'Signed', detail: 'Contract generated, banking added, both parties signed → Active.' },
  { id: 5, name: 'Execution', short: 'Delivering', detail: 'Consultant submits deliverables; PM reviews & approves.' },
  { id: 6, name: 'Payment', short: 'Paid', detail: 'Finance marks invoices paid for completed milestones.' },
  { id: 7, name: 'Closure', short: 'Closed', detail: 'PM initiates closure; Admin confirms → Closed.' },
  { id: 8, name: 'Archive', short: 'Archived', detail: 'Read-only retention.' },
]

function computeStageStatus(stageId, project, contracts, milestones) {
  const status = project?.status
  const hasConsultant = !!(project?.consultant_id || project?.consultantId)
  const hasActiveOrExpiredContract = (contracts || []).some(c => ['Active', 'Expired'].includes(c.status))
  const hasActiveContract = (contracts || []).some(c => c.status === 'Active')
  const hasAnyContract = (contracts || []).length > 0

  // A milestone counts as "done" if it has been approved, paid, or has an invoice triggered
  // (Rejected/zero-payment milestones are excluded from blocking)
  const paidMilestones = (milestones || []).filter(m => (m.payment_amount || m.amount_released_on_completion || 0) > 0)
  const anyMilestonesDone = paidMilestones.some(m =>
    ['Completed', 'Approved', 'Paid'].includes(m.status) || m.consultant_accepted || m.invoice_id
  )
  const allMilestonesPaid = paidMilestones.length > 0 && paidMilestones.every(m =>
    m.status === 'Paid' || ['Closed', 'Archived', 'Pending Closure'].includes(status)
  )
  const allMilestonesInvoiced = paidMilestones.length > 0 && paidMilestones.every(m =>
    m.invoice_id || ['Closed', 'Archived', 'Pending Closure'].includes(status)
  )

  // For project-status-based shortcuts: if project is closed/archived, stages 1-7 complete
  const isFinished = ['Closed', 'Archived', 'Pending Closure'].includes(status)

  if (stageId === 1) return 'completed'

  if (stageId === 2) {
    if (status === 'Pending Approval') return 'current'
    if (status === 'Rejected') return 'blocked'
    return 'completed'
  }

  if (stageId === 3) {
    if (status === 'Pending Approval') return 'upcoming'
    if (!hasConsultant) return 'current'
    return 'completed'
  }

  if (stageId === 4) {
    if (status === 'Pending Approval' || !hasConsultant) return 'upcoming'
    if (!hasAnyContract) return 'current'
    if (!hasActiveContract && !hasActiveOrExpiredContract) return 'current'
    return 'completed'
  }

  if (stageId === 5) {
    if (!hasAnyContract) return 'upcoming'
    if (isFinished || allMilestonesInvoiced) return 'completed'
    if (anyMilestonesDone) return 'completed'
    if (status === 'In Progress') return 'current'
    return 'upcoming'
  }

  if (stageId === 6) {
    if (['Closed', 'Archived'].includes(status)) return 'completed'
    if (allMilestonesPaid) return 'completed'
    if (allMilestonesInvoiced || status === 'Pending Closure') return 'current'
    return 'upcoming'
  }

  if (stageId === 7) {
    if (['Closed', 'Archived'].includes(status)) return 'completed'
    if (status === 'Pending Closure') return 'current'
    return 'upcoming'
  }

  if (stageId === 8) {
    if (status === 'Archived') return 'completed'
    if (status === 'Closed') return 'current'
    return 'upcoming'
  }

  return 'upcoming'
}

// ── Consultant-scoped lifecycle ────────────────────────────────────────────────
// A consultant owns only a slice of the project, so they see THEIR OWN journey
// (offered → contract → delivering → invoiced → paid), computed from their own
// milestones + their contract — not the whole-project stage. Management roles keep the
// full 8-stage project view above.
const CONSULTANT_STAGES = [
  { id: 1, name: 'Offered', short: 'Offered', detail: 'Milestone(s) offered to you on this project.' },
  { id: 2, name: 'Contract', short: 'Signed', detail: 'You signed and the admin activated your contract.' },
  { id: 3, name: 'Delivering', short: 'Delivering', detail: 'You submit deliverables; PM reviews & approves.' },
  { id: 4, name: 'Invoiced', short: 'Invoiced', detail: 'Invoice raised & confirmed for your approved work.' },
  { id: 5, name: 'Paid', short: 'Paid', detail: 'Finance has paid you — your work here is complete.' },
]

function computeConsultantStages(myMilestones, myContract) {
  const has = myMilestones.length > 0
  const contractActive = myContract && ['Active', 'Expired'].includes(myContract.status)
  const anyApproved = myMilestones.some(m => ['Approved', 'Paid'].includes(m.status) || m.consultant_accepted)
  const anyInvoiced = myMilestones.some(m => m.invoice_triggered || m.invoice_id || m.consultant_invoice_confirmed)
  const allPaid = has && myMilestones.every(m => m.status === 'Paid')

  const stateFor = (id) => {
    if (id === 1) return has ? 'completed' : 'current'
    if (id === 2) return contractActive ? 'completed' : (has ? 'current' : 'upcoming')
    if (id === 3) {
      if (anyApproved || anyInvoiced || allPaid) return 'completed'
      return contractActive ? 'current' : 'upcoming'
    }
    if (id === 4) {
      if (allPaid) return 'completed'
      if (anyInvoiced) return 'current'
      return 'upcoming'
    }
    if (id === 5) {
      if (allPaid) return 'completed'
      if (anyInvoiced) return 'current'
      return 'upcoming'
    }
    return 'upcoming'
  }
  return CONSULTANT_STAGES.map(s => ({ ...s, state: stateFor(s.id) }))
}

export default function ProjectLifecycleMap({ project, compact = false, refreshKey = 0 }) {
  const { user } = useAuth()
  const [contracts, setContracts] = useState([])
  const [milestones, setMilestones] = useState([])
  const [invoices, setInvoices] = useState([])

  useEffect(() => {
    if (!project?.id) return
    api.get(`/contracts/`).then(r => setContracts((r.data || []).filter(c => c.project_id === project.id))).catch(() => {})
    api.get(`/projects/${project.id}/milestones/`).then(r => setMilestones(r.data || [])).catch(() => {})
    // Invoices are derived from milestone state — no separate fetch needed
    // refreshKey lets a parent force a re-fetch after an action changes milestone/invoice
    // state (approval, invoice confirm/approve, payment) without project.id itself changing.
  }, [project?.id, refreshKey])

  if (!project) return null

  // Consultants see their OWN 5-stage journey; PM/Finance/Admin see the full project lifecycle.
  const myId = user?.id ?? user?.user_id
  const isConsultantView = user?.role === 'Consultant' && myId != null
  const myMilestones = isConsultantView
    ? (milestones || []).filter(m => (m.consultant_id ?? m.consultantId) === myId)
    : []
  const myContract = isConsultantView
    ? (contracts || []).find(c => (c.consultant_id ?? c.consultantId) === myId)
    : null

  const stages = isConsultantView
    ? computeConsultantStages(myMilestones, myContract)
    : STAGES.map(s => ({ ...s, state: computeStageStatus(s.id, project, contracts, milestones) }))
  const currentStage = stages.find(s => s.state === 'current') || stages.findLast?.(s => s.state === 'completed') || stages[0]
  const title = isConsultantView ? 'Your Progress on This Project' : 'Project Lifecycle Progress'

  const dot = (state) => {
    if (state === 'completed') return 'bg-green-500 text-white'
    if (state === 'current') return 'bg-blue-600 text-white ring-4 ring-blue-100 animate-pulse'
    if (state === 'blocked') return 'bg-red-500 text-white'
    return 'bg-gray-200 text-gray-400'
  }
  const line = (idx) => {
    const next = stages[idx + 1]
    if (!next) return ''
    if (stages[idx].state === 'completed' && next.state !== 'upcoming') return 'bg-green-400'
    return 'bg-gray-200'
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Currently at: <span className="font-medium text-blue-700">{currentStage?.name}</span>
            <span className="text-gray-400"> — {currentStage?.detail}</span>
          </p>
        </div>
      </div>

      {/* Horizontal stepper */}
      <div className="flex items-start justify-between gap-0">
        {stages.map((s, i) => (
          <div key={s.id} className="flex-1 flex flex-col items-center relative group">
            {/* Connector to next */}
            {i < stages.length - 1 && (
              <div className={`absolute top-4 left-1/2 right-0 h-0.5 -translate-y-1/2 ${line(i)}`}
                   style={{ width: 'calc(100% - 0px)' }} />
            )}
            {/* Dot */}
            <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${dot(s.state)}`}>
              {s.state === 'completed' ? <CheckCircle2 className="w-4 h-4" />
                : s.state === 'current' ? s.id
                : <Circle className="w-3 h-3" />}
            </div>
            {/* Label */}
            <div className="mt-2 text-center px-1">
              <p className={`text-xs font-medium ${s.state === 'current' ? 'text-blue-700' : s.state === 'completed' ? 'text-gray-700' : 'text-gray-400'}`}>
                {compact ? s.short : s.name}
              </p>
              {!compact && !isConsultantView && <p className="text-[10px] text-gray-400 mt-0.5">Stage {s.id}</p>}
            </div>
            {/* Tooltip on hover */}
            <div className="hidden group-hover:block absolute top-12 left-1/2 -translate-x-1/2 z-20 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-44 text-center shadow-lg">
              {s.detail}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
